import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'reactstrap';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const MAX_POLLS = 240; // ~8 min a 2s

// Pide año y mes, lanza el job del ZIP y hace polling hasta que S3 devuelve la URL.
// Lee la respuesta con tolerancia: si el servidor devuelve HTML (500, timeout,
// sesion vencida) se reporta el status en vez de reventar con SyntaxError.
async function readJson(res) {
    const text = await res.text();
    try {
        return JSON.parse(text);
    } catch (e) {
        return { __httpError: true, status: res.status, success:
            res.status === 500 ? "Error del servidor (500). Si acabas de desplegar, revisa que las migraciones se hayan ejecutado."
                               : `El servidor respondió ${res.status}`, type: "error" };
    }
}

export default function ModalMatrixReport({ modal, toggle, report, selectedIds }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [status, setStatus] = useState(null);   // null | pending | processing | done | error
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  // Periodo con el que se genero el ZIP que se esta ofreciendo para descargar.
  // No es el de los selects: el usuario puede moverlos despues de generar.
  const [generatedFor, setGeneratedFor] = useState({ year: null, month: null });
  const timerRef = useRef(null);
  const pollsRef = useRef(0);

  const token = document.querySelector("[name='csrf-token']").content;

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Al cambiar el periodo se descarta el resultado anterior: si no, quedaba el
  // boton "Descargar ZIP" del mes viejo junto al mes nuevo ya seleccionado.
  const resetResult = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setStatus(null);
    setUrl(null);
    setError(null);
    setProgress({ processed: 0, total: 0 });
  };

  const years = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 5; y--) years.push(y);

  const startPolling = (exportId) => {
    pollsRef.current = 0;
    timerRef.current = setInterval(() => {
      pollsRef.current += 1;
      if (pollsRef.current > MAX_POLLS) {
        clearInterval(timerRef.current);
        setStatus('error');
        setError('La generación está tardando demasiado. Revisa más tarde.');
        return;
      }

      fetch(`/formatos/matrix_report_export_status/${exportId}?_=${Date.now()}`, {
        method: 'GET', cache: 'no-store',
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
      })
      .then(readJson)
      .then(data => {
        setProgress({ processed: data.processed || 0, total: data.total || 0 });
        if (data.status === 'done') {
          clearInterval(timerRef.current);
          setStatus('done');
          setUrl(data.url);
        } else if (data.status === 'error') {
          clearInterval(timerRef.current);
          setStatus('error');
          setError(data.error || 'Ocurrió un error generando el ZIP');
        } else {
          setStatus(data.status);
        }
      })
      .catch(() => {});
    }, 2000);
  };

  const handleGenerate = () => {
    setStatus('pending');
    setError(null);
    setUrl(null);
    setGeneratedFor({ year, month });

    fetch('/formatos/matrix_report_export', {
      method: 'POST',
      body: JSON.stringify({
        report_id: report.id, year, month, selected_items: selectedIds
      }),
      headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
    })
    .then(readJson)
    .then(data => {
      if (!data || !data.id) {
        setStatus('error');
        setError((data && data.success) || 'No se pudo iniciar la generación');
        return;
      }
      setProgress({ processed: 0, total: data.total || selectedIds.length });
      startPolling(data.id);
    })
    .catch(() => { setStatus('error'); setError('Error de red'); });
  };

  const running = status === 'pending' || status === 'processing';

  return (
    <Modal isOpen={modal} toggle={toggle} className="modal-dialog-centered rk-modal" backdrop="static">
      <div className="rk-modal__header">
        <span className="rk-modal__header-icon"><i className="fas fa-table"></i></span>
        <h5>{report ? report.name : 'Reporte de matrices'}</h5>
        <button type="button" className="rk-modal__close" onClick={() => toggle()}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="rk-modal__body">
        <p style={{ fontSize: 13 }}>
          Se generará un PDF por cada uno de los <b>{selectedIds.length}</b> registro(s) seleccionado(s),
          con los datos del mes elegido, y se entregará un archivo ZIP.
        </p>

        <div className="row">
          <div className="col-md-6">
            <div className="rk-field">
              <label className="rk-label">Año</label>
              <select className="rk-input" value={year} disabled={running}
                onChange={(e) => { setYear(parseInt(e.target.value, 10)); resetResult(); }}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="rk-field">
              <label className="rk-label">Mes</label>
              <select className="rk-input" value={month} disabled={running}
                onChange={(e) => { setMonth(parseInt(e.target.value, 10)); resetResult(); }}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
          </div>

          {status === 'done' && (
            <div className="col-md-12">
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                Período generado: <b>{MONTHS[generatedFor.month - 1]} {generatedFor.year}</b>
              </div>
            </div>
          )}

          {running && (
            <div className="col-md-12">
              <div style={{ fontSize: 13 }}>
                <i className="fas fa-spinner fa-spin mr-2" />
                Generando… {progress.processed}/{progress.total || selectedIds.length}
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  Puedes cerrar esta ventana; el archivo se sigue generando en segundo plano.
                </div>
              </div>
            </div>
          )}

          {status === 'done' && (
            <div className="col-md-12">
              {url
                ? <a className="rk-btn rk-btn--primary" href={url} target="_blank" rel="noopener noreferrer">
                    <i className="fas fa-download"></i> Descargar ZIP
                  </a>
                : <span style={{ fontSize: 12, color: '#999' }}>No se recibió la URL del archivo.</span>}
            </div>
          )}

          {status === 'error' && (
            <div className="col-md-12">
              <div className="rk-alert">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rk-modal__footer">
        <button type="button" className="rk-btn rk-btn--ghost" onClick={() => toggle()}>
          Cerrar
        </button>
        {!running && status !== 'done' && (
          <button type="button" className="rk-btn rk-btn--primary" onClick={handleGenerate}
            disabled={selectedIds.length === 0}>
            <i className="fas fa-file-archive"></i> Generar ZIP
          </button>
        )}
      </div>
    </Modal>
  );
}
